import type { DocMigration } from "../docMigration";

const LabelableDocEntityTypes = [
  "ANNOTATION_TEMPLATE",
  "ANNOTATION",
  "CEILING",
  "PLAN_VIEW",
  "ELEVATION_VIEW",
  "SECTION_VIEW",
  "PROP",
  "PROP_INSTANCE",
  "SHEET",
  "CONTENT_TEMPLATE",
  "CONTENT_TEMPLATE_INSTANCE",
  "VIEW_TEMPLATE_OVERRIDE",
  "COMPONENT_INSTANCE",
  "ROOM",
  "ROOF",
  "FLOOR",
  "FLOOR_SYSTEM",
  "FOUNDATION",
  "STAIR_SYSTEM",
  "RAILING",
] as const;

const docEntityTypeToDocTableName = {
  GROUP: "groups",
  COMPONENT: "components",
  COMPONENT_INSTANCE: "componentInstances",
  DOC_REF: "docRefs",
  BUILDER_REF: "builderRefs",
  LEVEL: "levels",
  COMPOSITE_LEVEL: "compositeLevels",
  BLOCK: "blocks",
  ROOM: "rooms",
  STAIR_SYSTEM: "stairSystems",
  STAIR_BLOCK: "stairBlocks",
  RAILING: "railings",
  PRESET: "presets",
  ROOF_SYSTEM: "roofSystems",
  ROOF: "roofs",
  ROOF_VOLUME: "roofVolumes",
  FOUNDATION_SYSTEM: "foundationSystems",
  FOUNDATION: "foundations",
  UNDERLAY: "underlays",
  ASSET: "assets",
  REFERENCE_GEOM: "referenceGeom",
  ELEVATION: "elevations",
  PALETTE: "palettes",
  SETTINGS: "settings",
  SETTINGS_SET: "settingsSets",
  ENTITY_SETTINGS: "entitySettings",
  OPTION_TEMPLATE: "optionTemplates",
  SHEET: "sheets",
  DRAWING_REF: "drawingRefs",
  OPTION_SET_OVERRIDES: "optionSetOverrides",
  OPTION_SET: "optionSets",
  INSTANCE_OPTION_SET: undefined,
  OPTION_RULE: "optionRules",
  OPTION_EFFECT: "optionEffects",
  OPTION_LOCK: "optionLocks",
  PROP_INSTANCE: "propInstances",
  PROP: "propAttributes",
  PROP_DEF: "propDefs",
  CASEWORK: "casework",
  CEILING: "ceilings",
  EDGE_BEHAVIOR: "edgeBehaviors",
  OPENING: "openings",
  ANNOTATION: "annotation",
  FACADE: "facades",
  DORMER: "dormers",
  SITE: "sites",
  SITE_SHAPE: "siteShapes",
  MODIFIER: "modifiers",
  MEASUREMENT: "measurements",
  VIEW_3D: "view3Ds",
  ELEVATION_VIEW: "elevationViews",
  PLAN_VIEW: "planViews",
  OPTION_SET_REF: "optionSetRefs",
  ROOM_LAYOUT: "roomLayouts",
  ROOM_LAYOUT_INSTANCE: "roomLayoutInstances",
  SKYLIGHT: "skylights",
  ROOF_OPENING: "roofOpenings",
  COLUMN: "columns",
  FLOOR: "floors",
  FLOOR_FINISH: "floorFinishes",
  FACADE_SPAN_STRATUM: "facadeSpanStrata",
  FACADE_SWEEP_STRATUM: "facadeSweepStrata",
  RAILING_POST: "railingPosts",
  EDGE_MODIFIER: "edgeModifiers",
  ELECTRICAL_DEVICE: "electricalDevices",
  ELECTRICAL_LAYOUT: "electricalLayouts",
  PLAN_SET_TEMPLATE: "planSetTemplates",
  CONTENT_TEMPLATE: "contentTemplates",
  CONTENT_TEMPLATE_INSTANCE: "contentTemplateInstances",
  CONTENT_REPLICATOR: "contentReplicators",
  LOCAL_PARAMETER: "localParameters",
  GLOBAL_PARAMETER: "globalParameters",
  SYSTEM_PARAMETER: undefined,
  VIEWPORT: "viewports",
  VIEW_TEMPLATE: "viewTemplates",
  VIEW_TEMPLATE_OVERRIDE: "viewTemplateOverrides",
  DATA_TABLE: "dataTables",
  DATA_SEQUENCE: "dataSequences",
  SECTION_VIEW: "sectionViews",
  SELECTOR: "selectors",
  VIEW_OVERRIDE: "viewOverrides",
  VIEW_SELECTOR: "viewSelectors",
  PLAN_SET_TEMPLATE_CONFIG: "planSetTemplateConfigs",
  AUTO_DIMENSION: undefined,
  LABEL: "labels",
  REPORTABLE_UNIT_DESIGNATION: "reportableUnitDesignations",
  EFFECT: "effects",
  DATUM: "datums",
  RULE_BASED_SELECTOR: "ruleBasedSelectors",
  RULE_BASED_SELECTOR_INSTANCE: "ruleBasedSelectorInstances",
  ROOF_SIDE: undefined,
  OPTION: undefined,
  CATALOG_ITEM: undefined,
  ANNOTATION_TEMPLATE: undefined,
  ANNOTATION_TEMPLATE_COMPONENT: undefined,
  ELECTRICAL_RULE: undefined,
  SHAPE: undefined,
  EDGE: undefined,
  VERTEX: undefined,
  EDIT_MODE: undefined,
  GENERATED_ENTITY: undefined,
  INDETERMINATE_ENTITY_TYPE: undefined,
  FRAMING_COMPONENT: "framingComponents",
  FOUNDATION_ASSEMBLY: "foundationAssemblies",
  FOUNDATION_COMPONENT: "foundationComponents",
  HEADER_ASSEMBLY: "headerAssemblies",
  FRAME_FLOOR_ASSEMBLY: "frameFloorAssemblies",
  SLAB_FLOOR_ASSEMBLY: "slabFloorAssemblies",
  SLAB_COMPONENT: "slabComponents",
  SHEATHING_COMPONENT: "sheathingComponents",
  FLOOR_SYSTEM: "floorSystems",
};

const migration: DocMigration = {
  id: "20260402_add_labels_to_labelable_types",
  description: "Adds an empty label table to fields in the labelable type list",
  apply(json: any) {
    for (const entityType of LabelableDocEntityTypes) {
      // some labelable fields do not have a tableName, but are included for completeness.
      // docEntityTypeToDocTableName and LabelableDocEntityTypes are direct snapshots of
      // the corresponding tables in the code.
      const tableName = docEntityTypeToDocTableName[entityType];

      if (tableName) {
        const table = json[tableName] ?? {};
        for (const id of Object.keys(table)) {
          if (table[id]?.labels === undefined) {
            table[id].labels = {};
          }
        }
      }
    }

    return json;
  },
};

export default migration;
